import { z } from 'zod';
import { FormType } from '@ifa/types';

import { gridChecksumValidator } from './validators/grid-checksum-validator';
import { ridChecksumValidator } from './validators/rid-checksum-validator';

const validateRid = (value: string): boolean => value.match(/^\d{7}$|^\d{3}-\d{3}-\d{1}$/) !== null;

const gridOptionalValidator = (value: string | undefined): boolean => value?.length === 19 || value?.length === 0;

const gridRequiredValidator = (value: string): boolean => value.length === 19;

/**
 * Schema for the Patient Data Form
 *
 * rid entry is gated with regex only allowing numerics and hyphens
 *
 * donorId (GRID) entry is gated with regex only allowing alphanumeric characters
 */
export const SharedPatientDataFormSchema = z.object({
    formName: FormType,
    tcId: z.string({
        message: 'Please select a transplant center ID.',
    }),
    rid: z
        .string()
        .refine(data => validateRid(data), {
            message: 'Please enter Patient ID in 1111111 or 111-111-1 format.',
        })
        .transform(value => value?.replace(/-/g, '')) // remove hyphens after validation so that we can validate hyphens are formatted properly
        .refine(data => ridChecksumValidator(data), {
            message: 'Invalid Patient ID, please enter a valid Patient ID.',
        }),
    email: z.array(z.string().email().min(1)).min(1),
});

export const GridSchemaWithChecksumValidation = (
    lengthValidator: ((data: string) => boolean) | ((data?: string) => boolean)
) => {
    return z
        .string()
        .transform(value => value?.replace(/\s/g, ''))
        .refine(data => lengthValidator(data), {
            message: 'Please enter GRID using 19 characters.',
        })
        .refine(data => gridChecksumValidator(data), {
            message: 'Invalid GRID, please enter a valid GRID.',
        });
};

export const PatientDataFormSchema = z.discriminatedUnion('formName', [
    SharedPatientDataFormSchema.extend({
        formName: z.literal('F20'),
        // Grid is optional on the F20 Form
        donorId: GridSchemaWithChecksumValidation(gridOptionalValidator),
    }),
    SharedPatientDataFormSchema.extend({
        formName: z.literal('F10'),
        primaryOnly: z.boolean().refine(v => v === true),
        donorId: GridSchemaWithChecksumValidation(gridRequiredValidator),
    }),
]);

export type PatientDataForm = z.infer<typeof PatientDataFormSchema>;
